import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import type { FamilyProfile } from '@customTypes/user';

const SELF_PROFILE_KEY = 'self';

export interface ProfileSelection {
  changed: boolean;
  individualId?: string;
}

const individualIdFor = (familyProfiles: FamilyProfile[], identifier?: string) => {
  if (!identifier) return undefined;
  const selected = familyProfiles.find(profile => profile.identifier === identifier);
  return selected && !selected.is_self ? selected.identifier : undefined;
};

export const useSelectedProfile = (familyProfiles: FamilyProfile[]) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedIndividual = searchParams.get('individual') || undefined;
  const selectedIndividual = individualIdFor(familyProfiles, requestedIndividual);
  const selfProfile = familyProfiles.find(profile => profile.is_self);
  const activeProfileKey = selectedIndividual ?? selfProfile?.identifier ?? SELF_PROFILE_KEY;
  const activeProfile = familyProfiles.find(profile => profile.identifier === activeProfileKey);
  const isViewingSelf = !selectedIndividual;

  useEffect(() => {
    if (!requestedIndividual || familyProfiles.length === 0 || selectedIndividual) return;
    const next = new URLSearchParams(searchParams);
    next.delete('individual');
    setSearchParams(next, { replace: true });
  }, [familyProfiles.length, requestedIndividual, searchParams, selectedIndividual, setSearchParams]);

  const selectProfile = (identifier: string): ProfileSelection => {
    const individualId = individualIdFor(familyProfiles, identifier);
    if (individualId === selectedIndividual) return { changed: false, individualId };
    const next = new URLSearchParams(searchParams);
    if (individualId) next.set('individual', individualId);
    else next.delete('individual');
    setSearchParams(next, { replace: true });
    return { changed: true, individualId };
  };

  return {
    activeProfile,
    activeProfileKey,
    isViewingSelf,
    profiles: familyProfiles,
    selectProfile,
    selectedIndividual,
  };
};
